import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, StatusPill } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { initials } from '../lib/fmt';

type Row = { id: string; display_name: string; party_kind: string; email: string | null; phone: string | null; roles: string[] | null };

const ROLE_TONE: Record<string, 'inprogress' | 'open' | 'done' | 'onhold'> = {
  client: 'done', owner: 'onhold', management_company: 'inprogress', referrer: 'open',
  supplier: 'open', subcontractor: 'open', employee: 'inprogress', villa_manager: 'onhold', emergency_contact: 'open',
};

// The annuaire: every tiers (party) with its aggregated roles, from the `directory` view.
export function Directory() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('directory').select('id,display_name,party_kind,email,phone,roles').order('display_name').then((r) => {
      setRows((r.data as Row[]) ?? []); setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    const okQ = !q.trim() || r.display_name.toLowerCase().includes(q.toLowerCase()) || (r.email ?? '').toLowerCase().includes(q.toLowerCase());
    const okRole = !role || (r.roles ?? []).includes(role);
    return okQ && okRole;
  }), [rows, q, role]);

  const allRoles = useMemo(() => Array.from(new Set(rows.flatMap((r) => r.roles ?? []))).sort(), [rows]);

  return (
    <Layout header={<Topbar />}>
      <div className="px-7 pt-6"><h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.directory')}</h1></div>
      <div className="flex flex-col gap-[18px] px-7 pb-7 pt-[18px]">
        <div className="flex gap-2">
          <input className="h-9 flex-1 rounded-md border border-border bg-[#F7F8FA] px-3 text-sm outline-none" placeholder={t('directory.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="h-9 rounded-md border border-border bg-[#F7F8FA] px-2 text-sm" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">{t('directory.allRoles')}</option>
            {allRoles.map((r) => <option key={r} value={r}>{t('partyRole.' + r)}</option>)}
          </select>
        </div>
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead><tr className="text-left text-xs text-muted">
                <th className="px-5 py-3 font-medium">{t('col.name')}</th>
                <th className="px-5 py-3 font-medium">{t('directory.roles')}</th>
                <th className="px-5 py-3 font-medium">{t('form.email')}</th>
                <th className="px-5 py-3 font-medium">{t('form.phone')}</th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={4} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                : filtered.length === 0 ? <tr><td colSpan={4} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                : filtered.map((r) => (
                  <tr key={r.id} className="border-t border-divider hover:bg-bg">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-7 w-7 place-items-center rounded-full bg-segmented font-mono text-2xs">{initials(r.display_name)}</span>
                        <span className="font-medium">{r.display_name}</span>
                        <span className="text-2xs text-muted">{t('partyKind.' + r.party_kind)}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5"><div className="flex flex-wrap gap-1">{(r.roles ?? []).map((role) => <StatusPill key={role} tone={ROLE_TONE[role] ?? 'open'}>{t('partyRole.' + role)}</StatusPill>)}</div></td>
                    <td className="px-5 py-3.5 text-muted">{r.email ?? '—'}</td>
                    <td className="px-5 py-3.5 text-muted">{r.phone ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
